import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { List, Card, Modal, Form, Input, Radio } from 'antd';
import { Button } from '../baseComponents/Button';
const { TextArea } = Input;
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MenuOutlined,
} from '@ant-design/icons';
import { Select } from 'antd';

import { useDrag, useDrop } from 'react-dnd';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useAuth, useFeatureEntitlements } from '@frontegg/react';
import { formatOptions } from '../../utils/encounterConstant';
import { analytics } from 'src/services';
import { customFormValidationRules } from 'src/utils';

const sectionTypes = [
  { value: 'hpi', label: 'HPI' },
  { value: 'subjective', label: 'Subjective' },
  { value: 'assessment', label: 'Assessment' },
  { value: 'plan', label: 'Plan' },
  { value: 'assessment_plan', label: 'Assessment & Plan - Combined' },
  { value: 'other', label: 'Other' },
];

const formatTypes = [
  { value: 'prose', label: 'Prose' },
  { value: 'bulleted', label: 'Bulleted' },
  { value: 'itemized', label: 'Itemized' },
  { value: 'numbered', label: 'Numbered' },
  { value: 'problem_focused', label: 'Problem-Focused' },
];

const DraggableCard = ({ id, index, moveCard, children }) => {
  const ref = useRef(null);
  const [, drag] = useDrag(
    () => ({
      type: 'card',
      item: { id, index },
    }),
    [id, index]
  );

  const [, drop] = useDrop(
    {
      accept: 'card',
      hover(item) {
        if (!ref.current) {
          return;
        }
        const dragIndex = item.index;
        const hoverIndex = index;
        // Move only when the hover index and drag index are not the same
        if (dragIndex === hoverIndex) {
          return;
        }
        moveCard(dragIndex, hoverIndex);
        // Update the index for the dragged item to prevent unnecessary reordering
        item.index = hoverIndex;
      },
    },
    [index, moveCard]
  );

  drag(drop(ref));

  return <div ref={ref}>{children}</div>;
};

const TemplateBuilder = forwardRef(function TemplateBuilder(
  { template, onChange, dotPhrases, isDefaultTemplate },
  ref
) {
  const { isEntitled: hasGlobalNoteFormatEnabled } = useFeatureEntitlements(
    'global-note-format-preference'
  );

  const [editingSection, setEditingSection] = useState(null);
  const [sectionForm] = Form.useForm();
  const [outerForm] = Form.useForm();
  const { user } = useAuth();
  const { sectionType } = Form.useWatch([], sectionForm) || {};

  useImperativeHandle(ref, () => ({
    form: outerForm,
  }));

  const addSection = () => {
    analytics.track('Clicked Add New Section In Template Builder', {
      email: user?.email,
    });

    setEditingSection({
      order: template?.body?.sections.length + 1,
      index: -1,
      name: '',
      type: '',
      instructions: '',
    });
  };

  const editSection = (index) => {
    analytics.track('Clicked Edit Section In Template Builder', {
      email: user?.email,
    });
    setEditingSection({ ...template?.body?.sections[index], index });
  };

  const saveSection = async () => {
    const { name, type, instructions, sectionType, dotPhrase, format } =
      await sectionForm.validateFields();
    const { order, index } = editingSection;

    const getSectionTypeSpecificValues = () => {
      if (sectionType === 'instructions') {
        return { instructions, format };
      }

      const { dot_phrase_overrides_id, dot_phrase_id } = dotPhrases.find(
        (phrase) =>
          [phrase.dot_phrase_overrides_id, phrase.dot_phrase_id].includes(
            dotPhrase
          )
      ) || { dot_phrase_overrides_id: '', dot_phrase_id: '' };

      const values = {};

      if (dot_phrase_overrides_id) {
        values.dot_phrase_overrides_id = dot_phrase_overrides_id;
      }
      if (dot_phrase_id) {
        values.dot_phrase_id = dot_phrase_id;
      }
      return values;
    };

    const updatedSection = {
      name,
      type,
      order,
      ...getSectionTypeSpecificValues(),
    };

    let newSections = template?.body?.sections || [];
    if (index >= 0) {
      newSections = newSections.map((section, i) =>
        i === index ? updatedSection : section
      );
    } else {
      newSections = [...newSections, updatedSection];
    }
    onChange(
      {
        ...template,
        body: { ...template?.body, sections: newSections },
      },
      true
    );

    setEditingSection(null);
    sectionForm.resetFields();
  };

  const removeSection = (index) => {
    analytics.track('Removed A Section In Template Builder', {
      email: user?.email,
    });
    const newSections = template?.body?.sections.filter((_, i) => i !== index);
    onChange({
      ...template,
      body: { ...template?.body, sections: newSections },
    });
  };

  const moveSection = (dragIndex, hoverIndex) => {
    const totalSections = template?.body?.sections?.length;
    if (totalSections === 0) return;

    analytics.track('Used Drag And Drop In Template Builder', {
      email: user?.email,
    });
    const card = template?.body?.sections[dragIndex];
    const newSections = [...(template?.body?.sections || [])];
    newSections.splice(dragIndex, 1);
    newSections.splice(hoverIndex, 0, card);
    onChange({
      ...template,
      body: { ...template?.body, sections: newSections },
    });
  };

  useEffect(() => {
    if (!editingSection) return;

    const sectionType =
      editingSection.dot_phrase_overrides_id || editingSection.dot_phrase_id
        ? 'dot_phrase'
        : 'instructions';

    sectionForm.setFieldsValue({
      name: editingSection.name || '',
      type: editingSection.type || '',
      sectionType,
      ...(sectionType === 'instructions'
        ? {
            instructions: editingSection.instructions,
            format: editingSection.format || null,
            dotPhrase: null,
          }
        : {
            instructions: '',
            format: null,
            dotPhrase:
              editingSection.dot_phrase_overrides_id ||
              editingSection.dot_phrase_id,
          }),
    });
  }, [editingSection, sectionForm]);

  const templateId = template?.template_id || template?.template_overrides_id;
  const lastTemplateIdRef = useRef(null);
  useEffect(() => {
    // This useEffect is for setting the format and custom instructions when the component loads or when the template prop changes
    if (template) {
      if (lastTemplateIdRef.current !== templateId) {
        outerForm.setFieldsValue({
          name: template?.name,
          description: template?.description,
          format: template?.body?.format,
          custom_instructions: template?.body?.custom_instructions,
        });
        lastTemplateIdRef.current = templateId;
      }
    }
  }, [template, outerForm]);

  const getLabel = (label) => {
    return (
      <p className="font-sans text-md font-semibold text-[#000000]">{label}</p>
    );
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className={`w-full`}>
        <Form layout="vertical" form={outerForm}>
          <Form.Item
            className="mb-6"
            name="name"
            label={getLabel('Name')}
            rules={[
              {
                required: true,
                message: 'Please input the template name',
              },
              customFormValidationRules.noLeadingOrTrailingSpaces({
                message: 'Template name cannot have leading or trailing spaces',
              }),
            ]}
            onChange={(e) => {
              onChange({
                ...template,
                name: e.target.value,
              });
            }}
          >
            <Input className="font-sans" disabled={!!isDefaultTemplate} />
          </Form.Item>
          <Form.Item
            className="mb-12"
            name="description"
            label={getLabel('Description')}
          >
            <Input.TextArea
              rows={2}
              className="font-sans"
              disabled={!!isDefaultTemplate}
              onChange={(e) => {
                onChange({
                  ...template,
                  description: e.target.value,
                });
              }}
            />
          </Form.Item>
          <Form.Item
            className="mb-6 w-full"
            label={getLabel('Sections')}
            name="sections"
          >
            {!isDefaultTemplate ? (
              <div className="absolute right-0 top-[-40px]">
                <Button
                  type="button"
                  icon={<PlusOutlined />}
                  onClick={addSection}
                  disabled={!!isDefaultTemplate}
                >
                  Add section
                </Button>
              </div>
            ) : null}
            <List
              dataSource={template?.body?.sections}
              renderItem={(item, index) => (
                <div className={`mt-3`}>
                  {!isDefaultTemplate ? (
                    <DraggableCard
                      key={item.order}
                      id={item.order}
                      index={index}
                      moveCard={!isDefaultTemplate ? moveSection : ''}
                    >
                      <Card
                        hoverable={!isDefaultTemplate}
                        title={
                          <div className="flex items-center">
                            {!isDefaultTemplate ? (
                              <div className="flex flex-col">
                                <div className="cursor-move">
                                  <MenuOutlined className="text-gray-400" />
                                </div>
                              </div>
                            ) : null}
                            <div className="pl-4 font-sans">{item.name}</div>
                          </div>
                        }
                        extra={
                          <>
                            {!isDefaultTemplate ? (
                              <EditOutlined
                                className="ml-4"
                                onClick={() => editSection(index)}
                              />
                            ) : null}
                            {!isDefaultTemplate ? (
                              <DeleteOutlined
                                className="ml-4"
                                onClick={() => removeSection(index)}
                              />
                            ) : null}
                          </>
                        }
                        bodyStyle={{ padding: 0 }}
                        className="w-full"
                      />
                    </DraggableCard>
                  ) : (
                    <div className="p-4 rounded-lg border border-[#f0f0f0] hover:shadow-xl">
                      <span className="pl-4 font-semibold text-base">
                        {item.name}
                      </span>
                    </div>
                  )}
                </div>
              )}
            />
          </Form.Item>

          {!isDefaultTemplate && !hasGlobalNoteFormatEnabled ? (
            <Form.Item
              className="mb-6"
              label={getLabel('Format')}
              name="format"
            >
              <Radio.Group
                options={formatOptions}
                disabled={!!isDefaultTemplate}
                onChange={(e) => {
                  analytics.track(
                    'Changed Template Format In Template Builder',
                    { email: user?.email }
                  );
                  onChange({
                    ...template,
                    body: {
                      ...template?.body,
                      format: e.target.value,
                    },
                  });
                }}
              />
            </Form.Item>
          ) : null}

          {!isDefaultTemplate ? (
            <Form.Item
              label={getLabel('Custom Instructions')}
              name="custom_instructions"
            >
              <TextArea
                className="font-sans"
                rows={4}
                disabled={!!isDefaultTemplate}
                onChange={(e) => {
                  analytics.track(
                    'Changed Custom Instructions In Template Builder',
                    { email: user?.email }
                  );
                  onChange({
                    ...template,
                    body: {
                      ...template?.body,
                      custom_instructions: e.target.value,
                    },
                  });
                }}
              />
            </Form.Item>
          ) : null}
        </Form>

        <Modal
          centered={true}
          title={`${editingSection?.index >= 0 ? 'Edit' : 'Add'} Section`}
          open={!!editingSection}
          maskClosable={false}
          onCancel={() => setEditingSection(null)}
          footer={
            <div className="flex flex-row justify-end space-x-2">
              <Button
                className=" !bg-[#808080] hover:!bg-[#A9A9A9] !border-[#808080] hover:!border-[A9A9A9] "
                type="cancel"
                key="back"
                onClick={() => {
                  setEditingSection(null);
                  analytics.track(
                    'Clicked Cancel After Clicking Add Section In Template Builder',
                    { email: user?.email }
                  );
                }}
              >
                Cancel
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  key="save"
                  onClick={() => {
                    saveSection();
                    if (editingSection?.index >= 0) {
                      analytics.track('Updated Section In Template Builder', {
                        email: user?.email,
                      });
                    } else {
                      analytics.track('Added New Section In Template Builder', {
                        email: user?.email,
                      });
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          }
          className="settings-modal w-620px max-w-full"
        >
          <Form form={sectionForm} className="" layout="vertical">
            <Form.Item
              name="name"
              label={
                <p className="text-md font-medium text-[#000000]">
                  Section Name
                </p>
              }
              rules={[
                {
                  required: true,
                  message: 'Please input the section name',
                },
                customFormValidationRules.noLeadingOrTrailingSpaces({
                  message:
                    'Section name cannot have leading or trailing spaces',
                }),
              ]}
            >
              <Input id="section-name" />
            </Form.Item>
            {hasGlobalNoteFormatEnabled && (
              <Form.Item
                className="mb-2"
                name="type"
                label={
                  <p className="text-md font-medium text-[#000000]">
                    Section Type
                  </p>
                }
                rules={[
                  {
                    required: true,
                    message: 'Please select section type',
                  },
                ]}
              >
                <Select>
                  {sectionTypes.map((opt) => {
                    return (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}
            <Form.Item
              name="sectionType"
              label={
                !hasGlobalNoteFormatEnabled && (
                  <p className="text-md font-medium text-[#000000]">
                    Section Type
                  </p>
                )
              }
            >
              <Radio.Group
                defaultValue={'instructions'}
                onChange={(e) => {
                  analytics.track(
                    `Changed Section Type To ${e.target.value} In Add/Edit Section In Template Builder`,
                    { email: user?.email }
                  );
                }}
              >
                <Radio value={'instructions'}>Instructions</Radio>
                <Radio value={'dot_phrase'}>Dot phrase</Radio>
              </Radio.Group>
            </Form.Item>
            {sectionType === 'dot_phrase' && (
              <Form.Item
                name="dotPhrase"
                rules={[
                  {
                    required: true,
                    message: 'Please select a dot phrase',
                  },
                ]}
                label={
                  <p className="text-md font-medium text-[#000000]">
                    Dot Phrase
                  </p>
                }
              >
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="Select a dot phrase"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={() => {
                    analytics.track(
                      `Selected Dot Phrase Template In Add/Edit Section In Template Builder`,
                      { email: user?.email }
                    );
                  }}
                  className={`mt-1 border border-[#d9d9d9] bg-white w-full h-10 flex rounded-md items-center font-sans`}
                >
                  {dotPhrases?.map((phrase) => (
                    <Select.Option
                      key={
                        phrase.dot_phrase_overrides_id || phrase.dot_phrase_id
                      }
                      value={
                        phrase.dot_phrase_overrides_id || phrase.dot_phrase_id
                      }
                    >
                      {phrase.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {sectionType === 'instructions' && (
              <Form.Item
                name="format"
                label={
                  <p className="text-md font-medium text-[#000000]">Format</p>
                }
              >
                <Select placeholder="Select format">
                  {formatTypes.map((opt) => (
                    <Select.Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
            {sectionType === 'instructions' && (
              <Form.Item
                name="instructions"
                label={
                  <p className="text-md font-medium text-[#000000]">
                    Instructions
                  </p>
                }
              >
                <TextArea rows={16} className="font-sans" />
              </Form.Item>
            )}
          </Form>
        </Modal>
      </div>
    </DndProvider>
  );
});

export default TemplateBuilder;
